import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './SubscriptionCheckout.css';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Loader from '../../components/Loader/Loader';
import Alert from '../../components/Alert/Alert';
import secureAxios from '../../utils/secureAxios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import { faCheck, faCreditCard, faShieldAlt, faLock, faEye, faEyeSlash, faCalendarAlt, faTag, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';

export default function SubscriptionCheckout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Plan desde URL params o props
  const planFromUrl = searchParams.get('plan');

  // Estados principales
  const [plan, setPlan] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [planSlug, setPlanSlug] = useState('');
  const [error, setError] = useState(''); // 🆕 Estado para errores

  // Estados para Alert
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    type: 'success',
    title: '',
    message: '',
    redirectPath: '/'
  });

  // Estados del formulario
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [frontendPromoApplied, setFrontendPromoApplied] = useState(false); // 🆕 Para plan especial
  const [displayPricing, setDisplayPricing] = useState(null); // 🆕 Para mostrar precios visuales
  const [userEmail, setUserEmail] = useState('');
  const [cardData, setCardData] = useState({
    number: '',
    cvc: '',
    exp_month: '',
    exp_year: '',
    card_holder: ''
  });
  const [showCVC, setShowCVC] = useState(false);

  // Estados de aceptación
  const [acceptances, setAcceptances] = useState({
    terms: false,
    dataProcessing: false
  });

  // Tokens de Wompi
  const [acceptanceTokens, setAcceptanceTokens] = useState(null);

  // Configuración de Wompi
  const WOMPI_PUBLIC_KEY = 'pub_prod_TCMCxT1ysVBvgcy4qZc7SgmoiVLJ6B7u';
  const WOMPI_API_URL = 'https://production.wompi.co/v1';

  const loadPlanData = async (planId) => {
    try {
      const response = await axios.get(`https://api.minymol.com/subscription-plans/${planId}`);

      // El response.data ya es el plan directamente
      const planData = response.data;

      setPlanSlug(planData.slug);

      setPlan(planData);
      
      // Guardar precios reales del backend (para enviar a Wompi)
      setPricing({
        originalPrice: planData.pricing.originalPrice,
        finalPrice: planData.pricing.finalPrice,
        hasPromo: planData.pricing.hasPromo,
        promoMessage: planData.pricing.promoMessage
      });

      // Si es el plan especial, ajustar SOLO la visualización
      if (planId === 'provider-intro-special') {
        setDisplayPricing({
          originalPrice: 1050000,
          finalPrice: 1050000,
          hasPromo: false,
          promoMessage: null
        });
      } else {
        // Para otros planes, mostrar lo mismo que el pricing real
        setDisplayPricing({
          originalPrice: planData.pricing.originalPrice,
          finalPrice: planData.pricing.finalPrice,
          hasPromo: planData.pricing.hasPromo,
          promoMessage: planData.pricing.promoMessage
        });
      }
    } catch (error) {
      console.error('Error cargando plan:', error);
      throw error;
    }
  };

  const checkCurrentAccess = useCallback(async () => {
    try {
      const response = await secureAxios.get(`/plan-subscriptions/check/${planSlug}`);
      setHasAccess(response.data.hasAccess || false);
    } catch (error) {
      console.error('Error verificando acceso:', error);
      setHasAccess(false);
    }
  }, [planSlug]);

  // Cargar datos iniciales
  useEffect(() => {
    const loadUserAndPlan = async () => {
      try {
        // Cargar plan específico
        await loadPlanData(planFromUrl);
        // Cargar tokens de Wompi
        await loadAcceptanceTokens();
      } catch (error) {
        console.error('Error cargando datos:', error);
      } finally {
        setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        if (planFromUrl) {
          loadUserAndPlan();
        } else {
          navigate('/');
        }
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [planFromUrl, navigate]);

  useEffect(() => {
    if (planSlug) {
      checkCurrentAccess();
    }
  }, [planSlug, checkCurrentAccess]);

  const loadAcceptanceTokens = async () => {
    try {
      const response = await secureAxios.get('/subscriptions/payments/acceptance-tokens');
      setAcceptanceTokens(response.data.data);
    } catch (error) {
      console.error('Error cargando tokens de aceptación:', error);
    }
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;

    setProcessing(true);
    try {
      // Manejo especial para el plan provider-intro-special (SOLO VISUAL)
      if (planFromUrl === 'provider-intro-special' && promoCode.toUpperCase() === atob('UFJPTU81MA==')) {
        // Aplicar descuento SOLO en la visualización
        setFrontendPromoApplied(true);
        setDisplayPricing({
          originalPrice: 1050000,
          finalPrice: 525000,
          hasPromo: true,
          promoMessage: '50% de descuento aplicado'
        });
        setPromoResult({
          success: true,
          message: '¡Código promocional aplicado! 50% de descuento'
        });
        setProcessing(false);
        // NO modificamos pricing real, se mantiene con los valores del backend
        return;
      }

      // Para otros planes, usar la lógica normal del backend
      const response = await secureAxios.post('/subscription-plans/apply-promo', {
        promoCode: promoCode.toUpperCase(),
        planId: plan.id
      });

      if (response.data.success) {
        const result = response.data;
        setPromoResult(result);
        // Actualizar precios reales
        setPricing({
          ...pricing,
          finalPrice: result.finalPrice,
          hasPromo: true,
          promoMessage: result.message
        });
        // Actualizar también la visualización
        setDisplayPricing({
          ...pricing,
          finalPrice: result.finalPrice,
          hasPromo: true,
          promoMessage: result.message
        });
      } else {
        setPromoResult({
          success: false,
          message: response.data.message || 'Código promocional inválido'
        });
      }
    } catch (error) {
      console.error('Error aplicando código promocional:', error);
      setPromoResult({
        success: false,
        message: error.response?.data?.message || 'Error aplicando código promocional'
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleCardInputChange = (field, value) => {
    let formattedValue = value;

    if (field === 'number') {
      // Formatear número de tarjeta con espacios (máximo 16 dígitos)
      const cleaned = value.replace(/\s/g, '');
      if (cleaned.length <= 16) {
        formattedValue = cleaned.replace(/(.{4})/g, '$1 ').trim();
      } else {
        return;
      }
    } else if (field === 'exp_month') {
      // Solo números para mes (máximo 2 dígitos)
      formattedValue = value.replace(/\D/g, '').slice(0, 2);
      // Validar que no sea mayor a 12
      if (parseInt(formattedValue) > 12) {
        formattedValue = '12';
      }
    } else if (field === 'exp_year') {
      // Solo números para año (máximo 4 dígitos)
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    } else if (field === 'cvc') {
      // Solo números para CVC (máximo 4 dígitos)
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    }

    setCardData(prev => ({
      ...prev,
      [field]: formattedValue
    }));
  };

  // Función para detectar el tipo de tarjeta
  const getCardType = (number) => {
    const cleaned = number.replace(/\s/g, '');
    if (/^4/.test(cleaned)) return 'visa';
    if (/^5[1-5]/.test(cleaned)) return 'mastercard';
    if (/^3[47]/.test(cleaned)) return 'amex';
    return 'generic';
  };

  // Generar opciones de años (año actual + 15 años)
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 16; i++) {
      years.push(currentYear + i);
    }
    return years;
  };

  // Generar opciones de meses
  const getMonthOptions = () => {
    return Array.from({ length: 12 }, (_, i) => {
      const month = (i + 1).toString().padStart(2, '0');
      return { value: month, label: month };
    });
  };

  const handleAcceptanceChange = (field) => {
    setAcceptances(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    // Validar email
    if (!userEmail.trim()) return 'Email requerido';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail)) return 'Email inválido';

    // Validar datos de tarjeta solo si hay precio
    if (pricing?.finalPrice > 0) {
      const cardNumber = cardData.number.replace(/\s/g, '');
      if (cardNumber.length < 16) return 'Número de tarjeta inválido';
      if (!cardData.card_holder.trim()) return 'Nombre del titular requerido';
      if (!cardData.exp_month || cardData.exp_month.length !== 2) return 'Mes de expiración inválido';
      if (!cardData.exp_year || cardData.exp_year.length !== 4) return 'Año de expiración inválido';
      if (!cardData.cvc || cardData.cvc.length < 3) return 'CVC inválido';
    }

    // Validar aceptaciones
    if (!acceptances.terms) return 'Debe aceptar los términos y condiciones';
    if (!acceptances.dataProcessing) return 'Debe aceptar el tratamiento de datos';

    return null;
  };

  const createCardToken = async (cardData) => {
    try {
      const response = await fetch(`${WOMPI_API_URL}/tokens/cards`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${WOMPI_PUBLIC_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          number: cardData.number.replace(/\s/g, ''),
          exp_month: cardData.exp_month,
          exp_year: cardData.exp_year.slice(-2), // Solo últimos 2 dígitos
          cvc: cardData.cvc,
          card_holder: cardData.card_holder
        })
      });

      const result = await response.json();

      if (result.status === 'CREATED') {
        return result.data.id; // Token de la tarjeta
      } else {
        throw new Error(result.error?.reason || 'Error creando token de tarjeta');
      }
    } catch (error) {
      console.error('Error tokenizando tarjeta:', error);
      throw new Error('Error procesando la tarjeta. Verifique los datos ingresados.');
    }
  };

  const processSubscription = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(''); // Limpiar errores previos
    setProcessing(true);
    try {
      if (pricing.finalPrice === 0) {
        // Suscripción gratuita
        await createFreeSubscription();
      } else {
        // Suscripción con pago
        await createPaidSubscription();
      }
    } catch (error) {
      console.error('Error procesando suscripción:', error);
      setError(error.message || 'Error procesando el pago. Por favor, intenta nuevamente.');
    } finally {
      setProcessing(false);
    }
  };

  const createFreeSubscription = async () => {
    try {
      const subscriptionData = {
        planId: plan.id,
        customerEmail: userEmail,
        acceptanceToken: acceptanceTokens.presigned_acceptance.acceptance_token,
        acceptPersonalAuth: acceptanceTokens.presigned_personal_data_auth.acceptance_token,
        promoCode: promoCode.trim() || null
      };

      const response = await secureAxios.post('/subscriptions/payments/subscribe-free', subscriptionData);

      // Verificar si la respuesta contiene los datos esperados
      if (response.data && (response.data.success || response.data.subscriptionId)) {
        // Mostrar Alert de éxito con redirección a home
        setAlertConfig({
          type: 'success',
          title: '¡Suscripción Activada!',
          message: '¡Tu suscripción ha sido activada exitosamente!',
          redirectPath: '/'
        });
        setShowAlert(true);
      } else {
        throw new Error(response.data.message || 'Error activando suscripción gratuita');
      }
    } catch (error) {
      console.error('Error creando suscripción gratuita:', error);
      throw new Error(error.response?.data?.message || 'Error activando suscripción gratuita');
    }
  };

  const createPaidSubscription = async () => {
    try {
      // Paso 1: Crear token de tarjeta en Wompi
      const cardToken = await createCardToken(cardData);

      // Paso 2: Crear suscripción con el token
      const subscriptionData = {
        planId: plan.id,
        cardToken: cardToken,
        acceptanceToken: acceptanceTokens.presigned_acceptance.acceptance_token,
        acceptPersonalAuth: acceptanceTokens.presigned_personal_data_auth.acceptance_token,
        enableRecurring: true,
        savePaymentSource: true,
        customerEmail: userEmail
      };

      const response = await secureAxios.post('/subscriptions/payments/subscribe-with-card', subscriptionData);

      // Verificar si la respuesta contiene los datos esperados
      if (response.data && response.data.subscriptionId && response.data.transactionId) {
        // Mostrar Alert de éxito con redirección a home
        setAlertConfig({
          type: 'success',
          title: '¡Pago Procesado!',
          message: '¡Tu pago ha sido procesado exitosamente! Tu suscripción está siendo activada.',
          redirectPath: '/'
        });
        setShowAlert(true);
      } else {
        throw new Error('Respuesta inválida del servidor');
      }
    } catch (error) {
      console.error('Error creando suscripción pagada:', error);
      throw new Error(error.response?.data?.message || error.message || 'Error procesando el pago');
    }
  };

  if (loading) return <Loader />;

  if (hasAccess) {
    return (
      <div className="subscription-checkout">
        <Header />
        <div className="checkout-container">
          <div className="access-granted">
            <div className="access-granted-content">
              <FontAwesomeIcon icon={faCheck} className="check-icon" />
              <h2>¡Ya tienes acceso a SAI!</h2>
              <p>Tu suscripción está activa y puedes acceder a la plataforma.</p>
              <button onClick={() => navigate('/sai')} className="access-button">
                Ir a Plataforma SAI
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="subscription-checkout">
        <Header />
        <div className="checkout-container">
          <div className="error-message">
            <h2>Plan no encontrado</h2>
            <button onClick={() => navigate('/')} className="back-button">
              Volver al inicio
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Header />
      <Alert 
        show={showAlert} 
        {...alertConfig} 
        onClose={() => setShowAlert(false)} 
      />
      <div className="checkout-container">
        <div className="checkout-content">
          {/* Información del Plan */}
          <div className="plan-summary">
            <div className="plan-header">
              <h1>{plan.name}</h1>
              <p className="plan-description">{plan.description}</p>
            </div>

            <div className="pricing-display">
              {displayPricing?.hasPromo && planFromUrl !== 'provider-intro-special' && (
                <span className="original-price">${displayPricing.originalPrice?.toLocaleString()}</span>
              )}
              {displayPricing?.hasPromo && planFromUrl === 'provider-intro-special' && (
                <span className="original-price">${displayPricing.originalPrice?.toLocaleString()}</span>
              )}
              <span className="final-price">${displayPricing?.finalPrice?.toLocaleString()}</span>
              <span className="duration">/{
                plan.duration === 'yearly' ? 'año' :
                plan.duration === 'monthly' ? 'mes' :
                plan.duration === 'quarterly' ? 'trimestre' :
                plan.duration === 'biannual' ? 'semestre' :
                plan.duration === 'lifetime' ? 'pago único' :
                plan.duration
              }</span>
              {displayPricing?.promoMessage && planFromUrl !== 'provider-intro-special' && (
                <div className="promo-badge">{displayPricing.promoMessage}</div>
              )}
              {displayPricing?.promoMessage && planFromUrl === 'provider-intro-special' && frontendPromoApplied && (
                <div className="promo-badge">{displayPricing.promoMessage}</div>
              )}
            </div>

            <div className="plan-features">
              <h3>Incluye:</h3>
              <ul>
                {plan.features?.map((feature, index) => (
                  <li key={index}>
                    <FontAwesomeIcon icon={faCheck} />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Formulario de Pago */}
          <div className="payment-form">
            {/* Código Promocional */}
            <div className="promo-section">
              <h3>
                <FontAwesomeIcon icon={faTag} />
                Código Promocional
              </h3>
              <div className="promo-input-group">
                <div className="input-with-icon">
                  <FontAwesomeIcon icon={faTag} className="input-icon" />
                  <input
                    type="text"
                    placeholder="Código promocional (ej: SAI2025)"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    className="promo-input"
                  />
                </div>
                <button
                  onClick={applyPromoCode}
                  disabled={processing || !promoCode.trim()}
                  className="promo-button"
                >
                  Aplicar
                </button>
              </div>
              {promoResult && (
                <div className={`promo-result ${promoResult.success ? 'success' : 'error'}`}>
                  <FontAwesomeIcon icon={promoResult.success ? faCheck : faLock} />
                  {promoResult.message}
                </div>
              )}
            </div>

            {/* Información del Usuario */}
            <div className="user-section">
              <h3>
                <FontAwesomeIcon icon={faEnvelope} />
                Información del Usuario
              </h3>
              <div className="form-group">
                <label>Correo Electrónico</label>
                <div className="input-with-icon">
                  <input
                    type="email"
                    placeholder="tu-email@ejemplo.com"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    className="card-input"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Información de Tarjeta */}
            {pricing?.finalPrice > 0 && (
              <div className="card-section">
                <h3>
                  <FontAwesomeIcon icon={faCreditCard} />
                  Información de Pago
                </h3>

                <div className="card-form">
                  <div className="form-group">
                    <label>Número de Tarjeta</label>
                    <div className="card-number-input">
                      <input
                        type="text"
                        placeholder="1234 5678 9012 3456"
                        value={cardData.number}
                        onChange={(e) => handleCardInputChange('number', e.target.value)}
                        maxLength="19"
                        className="card-input with-card-icon"
                      />
                      <div className={`card-type-icon ${getCardType(cardData.number)}`}>
                        {getCardType(cardData.number) === 'visa' && (
                          <span className="card-brand">VISA</span>
                        )}
                        {getCardType(cardData.number) === 'mastercard' && (
                          <span className="card-brand">MC</span>
                        )}
                        {getCardType(cardData.number) === 'amex' && (
                          <span className="card-brand">AMEX</span>
                        )}
                        {getCardType(cardData.number) === 'generic' && (
                          <FontAwesomeIcon icon={faCreditCard} />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Nombre del Titular</label>
                    <input
                      type="text"
                      placeholder="Nombre como aparece en la tarjeta"
                      value={cardData.card_holder}
                      onChange={(e) => handleCardInputChange('card_holder', e.target.value.toUpperCase())}
                      className="card-input"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        Mes
                      </label>
                      <select
                        value={cardData.exp_month}
                        onChange={(e) => handleCardInputChange('exp_month', e.target.value)}
                        className="card-input select-input"
                      >
                        <option value="">MM</option>
                        {getMonthOptions().map(month => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        Año
                      </label>
                      <select
                        value={cardData.exp_year}
                        onChange={(e) => handleCardInputChange('exp_year', e.target.value)}
                        className="card-input select-input"
                      >
                        <option value="">AAAA</option>
                        {getYearOptions().map(year => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faLock} />
                        CVC
                      </label>
                      <div className="cvc-input-wrapper">
                        <input
                          type={showCVC ? "text" : "password"}
                          placeholder="123"
                          value={cardData.cvc}
                          onChange={(e) => handleCardInputChange('cvc', e.target.value)}
                          maxLength="4"
                          className="card-input cvc-input"
                        />
                        <button
                          type="button"
                          className="toggle-cvc"
                          onClick={() => setShowCVC(!showCVC)}
                        >
                          <FontAwesomeIcon icon={showCVC ? faEyeSlash : faEye} />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="card-info-helper">
                    <FontAwesomeIcon icon={faShieldAlt} />
                    <span>Tus datos están protegidos con encriptación de nivel bancario</span>
                  </div>
                </div>
              </div>
            )}

            {/* Términos y Condiciones */}
            <div className="terms-section">
              <h3>Términos y Condiciones</h3>

              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={acceptances.terms}
                    onChange={() => handleAcceptanceChange('terms')}
                  />
                  <span className="checkmark"></span>
                  <span className='terms-text'>Acepto haber leído los <a href={acceptanceTokens?.presigned_acceptance?.permalink} target="_blank" rel="noreferrer">reglamentos y la política de privacidad</a> para hacer este pago.</span>
                </label>
              </div>

              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={acceptances.dataProcessing}
                    onChange={() => handleAcceptanceChange('dataProcessing')}
                  />
                  <span className="checkmark"></span>
                  <span className='terms-text'>Acepto la <a href={acceptanceTokens?.presigned_personal_data_auth?.permalink} target="_blank" rel="noreferrer">autorización para la administración de datos personales</a>.</span>
                </label>
              </div>

            </div>

            {/* Seguridad Wompi */}
            <div className="security-section">
              <div className="wompi-info">
                <img
                  src="https://login.wompi.co/_next/image?url=%2Fassets%2Fimages%2Fwompi-logo-black.png&w=256&q=75"
                  alt="Wompi"
                  className="wompi-logo"
                />
                <div className="security-text">
                  <p>
                    <FontAwesomeIcon icon={faShieldAlt} />
                    Esta plataforma utiliza Wompi para procesar pagos de forma segura
                  </p>
                  <p>
                    <FontAwesomeIcon icon={faLock} />
                    Tus datos están protegidos con encriptación de nivel bancario
                  </p>
                </div>
              </div>
            </div>

            {/* Mostrar errores */}
            {error && (
              <div className="error-message-box">
                <FontAwesomeIcon icon={faLock} />
                <span>{error}</span>
              </div>
            )}

            {/* Botón de Suscripción */}
            <button
              onClick={processSubscription}
              disabled={processing}
              className="subscribe-button"
            >
              {processing ? (
                <>
                  <div className="spinner"></div>
                  Procesando...
                </>
              ) : (
                displayPricing?.finalPrice === 0 ?
                  'Activar Suscripción Gratuita' :
                  `Suscribirse - $${displayPricing?.finalPrice?.toLocaleString()}`
              )}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
